"use client"

import { useEffect, useRef, useState } from "react"
import CdnImage from "@modules/common/components/cdn-image"
import type { ImageProps } from "next/image"

// Accepts CdnImage's `optimize` opt-in on top of the standard next/image props so
// callers (e.g. product cards) can route through the Next.js optimizer.
type ImageWithLoaderProps = ImageProps & { optimize?: boolean }

// Shimmer wave shown while the image is downloading, then the image fades in.
// Wraps CdnImage so all existing unoptimized/CDN-bypass logic is preserved.
export default function ImageWithLoader(props: ImageWithLoaderProps) {
  const [loaded, setLoaded] = useState(false)
  const imgRef = useRef<HTMLImageElement>(null)

  // Cache-race guard: if the <img> already painted (served from HTTP/bfcache, or
  // completed between SSR and hydration) before React attached `onLoad`, the load
  // event never fires — leaving the shimmer stuck on top with the image at
  // opacity-0 (the "image loaded but the loader keeps spinning" bug). Detect an
  // already-finished image on mount and clear the shimmer.
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true)
    }
  }, [])

  return (
    <>
      {/* Shimmer placeholder — hidden once image has painted */}
      {!loaded && (
        <span
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, #F0EDE9 25%, #E8E4DF 50%, #F0EDE9 75%)",
            backgroundSize: "200% 100%",
            animation: "alif-shimmer 1.6s ease-in-out infinite",
          }}
        />
      )}

      <CdnImage
        ref={imgRef}
        {...props}
        className={[
          props.className ?? "",
          "transition-opacity duration-[400ms]",
          loaded ? "opacity-100" : "opacity-0",
        ]
          .join(" ")
          .trim()}
        onLoad={() => setLoaded(true)}
        // Never leave the shimmer spinning forever on a failed/aborted image —
        // clear it so the image (or its alt text) shows honestly instead.
        onError={() => setLoaded(true)}
      />
    </>
  )
}
